import React, { useEffect, useRef } from 'react'

import { trackRecommendEvent } from '@/lib/recommendTracking'
import { recordRecommendClick } from '@/lib/sessionTracking'

type Props = {
  /** Emplacement du bloc (ex. home:bestsellers). Sans lui, la carte s'affiche sans mesure. */
  placement?: string
  productId: string | number
  /** Position (1-based) du produit dans le bloc */
  rank: number
  className?: string
  children: React.ReactNode
}

// Standard d'une impression visible : la moitié de la carte, pendant une seconde. Sans ce délai,
// le repli affiché le temps que le bloc charge serait compté comme vu.
const VISIBLE_RATIO = 0.5
const VISIBLE_MS = 1000

// Une carte n'est comptée qu'une fois par page : replier puis déplier un bloc la remonte.
const alreadySeen = new Set<string>()

/**
 * Entoure une carte d'un bloc recommandé et mesure ce qu'il s'y passe : carte vue à l'écran,
 * clic vers la fiche, clic sur le bouton panier (marqué `data-rec-add`).
 */
const RecommendSlot: React.FC<Props> = ({ placement, productId, rank, className, children }) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!placement || !element || typeof IntersectionObserver === 'undefined') return

    const key = `${window.location.pathname}|${placement}|${productId}`
    if (alreadySeen.has(key)) return

    let timer: ReturnType<typeof setTimeout> | undefined
    const observer = new IntersectionObserver(
      entries => {
        clearTimeout(timer)
        if (!entries.some(entry => entry.isIntersecting)) return
        timer = setTimeout(() => {
          observer.disconnect()
          if (alreadySeen.has(key)) return
          alreadySeen.add(key)
          trackRecommendEvent('rec_impression', productId, placement, rank)
        }, VISIBLE_MS)
      },
      { threshold: VISIBLE_RATIO }
    )
    observer.observe(element)

    return () => {
      clearTimeout(timer)
      observer.disconnect()
    }
  }, [placement, productId, rank])

  const handleClickCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!placement) return

    const target = event.target as HTMLElement
    if (target.closest('[data-rec-add]')) {
      trackRecommendEvent('rec_add_to_cart', productId, placement, rank)
      return
    }

    if (target.closest('a')?.getAttribute('href')?.includes('/products/')) {
      trackRecommendEvent('rec_click', productId, placement, rank)
      recordRecommendClick(placement, rank)
    }
  }

  return (
    <div ref={ref} onClickCapture={handleClickCapture} className={className}>
      {children}
    </div>
  )
}

export default RecommendSlot
